import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { ICheckpointAction, ICheckpointActionAlertRecipient } from '@fi-shared/checklist/checklist-v2';
import { CheckpointActionRecipientType, CheckpointActionType } from '@fi-shared/checklist/checklist-enum';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklist2ReportHelperService } from '../../../services/fi-checklist2-report-helper.service';
import { ITask } from '@fi-shared/fi-task-shared/fi-task-shared';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { IChecklist2ReportAnswer } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportDeviationTaskType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { BehaviorSubject } from 'rxjs';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';
import { ObjectTypes } from '@fi-shared/types';

export interface IDeviationTasksDialogData {
    answerDetailsReport: IChecklist2ReportAnswer;
    taskType: FiChecklist2ReportDeviationTaskType;
    accessRightFilterCollection: IAccessRightFilterCollection;
    canEditDeleteAutomatedTaskFn: () => boolean;
    canEditDeleteOneTimeTaskFn: (task: ITaskV2) => boolean;
    canCreateAutomatedTask: boolean;
    canCreateOneTimeTask: boolean;
    hasAccessToAutomatedTasks: boolean;
    hasAccessToOneTimeTasks: boolean;
    isTaskV2: boolean;
}

@Component({
    selector: 'fi-deviation-tasks-dialog',
    templateUrl: './deviation-tasks-dialog.component.html',
    styleUrls: ['./deviation-tasks-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-deviation-tasks-dialog' },
    standalone: false,
})
export class FiDeviationTasksDialogComponent
    extends FiDialogsContentBase<IDeviationTasksDialogData, void>
    implements OnInit
{
    selectedTabIndex: number;
    readonly AutomatedTaskTabIndex: number = 0;
    readonly OneTimeTaskTabIndex: number = 1;
    protected oneTimeTasks$ = new BehaviorSubject<ITaskV2[]>(undefined);

    ngOnInit() {
        this.setServices();
        this.setSelectedTabIndex();
        this.fiTaskSharedService
            .getTasksByIds(this.data.answerDetailsReport.OneTimeTaskIds, this.data.isTaskV2)
            .then((tasks) => {
                this.oneTimeTasks$.next(tasks.filter((task) => !task.IsArchived));
            });
    }

    protected canEditDeleteAutomatedTask(): boolean {
        return this.data.canEditDeleteAutomatedTaskFn();
    }

    protected canEditDeleteOneTimeTask(task: ITaskV2): boolean {
        return this.data.canEditDeleteOneTimeTaskFn(task);
    }

    protected canCreateAutomatedTask() {
        return this.data.canCreateAutomatedTask;
    }

    protected canCreateOneTimeTask() {
        return this.data.canCreateOneTimeTask;
    }

    protected hasAccessToAutomatedTasks(): boolean {
        return this.data.hasAccessToAutomatedTasks;
    }

    protected hasAccessToOneTimeTasks(): boolean {
        return this.data.hasAccessToOneTimeTasks;
    }

    private setSelectedTabIndex() {
        this.selectedTabIndex =
            this.data.taskType === FiChecklist2ReportDeviationTaskType.AutomatedTask
                ? this.AutomatedTaskTabIndex
                : this.OneTimeTaskTabIndex;
    }

    editAutomatedTask(action: ICheckpointAction) {
        this.getCheckpointActionEditorDialogByType(action)
            .onYesButtonClick((updatedAction: ICheckpointAction) => {
                const actionIndex: number = this.data.answerDetailsReport.CheckpointActions.indexOf(action);
                this.saveSingleCheckpointAction(this.data.answerDetailsReport, updatedAction, actionIndex);
            })
            .afterClosed(() => (this.saveChangeWarningService.closeDialogOnTransition = false));
        this.saveChangeWarningService.closeDialogOnTransition = true;
    }

    saveSingleCheckpointAction(
        answerDetailsReport: IChecklist2ReportAnswer,
        action: ICheckpointAction,
        actionIndex: number,
    ) {
        this.fiChecklist2Service
            .saveSingleCheckpointAction(answerDetailsReport.CheckpointId, action)
            .then((actionId: number) => {
                action.Id = actionId;
                this.data.answerDetailsReport.CheckpointActions[actionIndex] = action;
                if (action.Action === CheckpointActionType.SendNotification) {
                    this.fiChecklistHelperService.showNotificationSavedSnackbar();
                } else {
                    this.fiChecklistHelperService.showActionAddedSnackbar(
                        action.Settings.TaskName,
                        answerDetailsReport.CheckpointName,
                    );
                }
                this.fiChecklist2ReportHelperService.triggerCheckpointActionsChanged(this.data.answerDetailsReport);
                this.cdr.markForCheck();
            });
    }

    openConfirmDeleteAutomatedTaskDialog(action: ICheckpointAction) {
        this.fiCheckpoint2HelperService
            .getConfirmDeleteCheckpointActionsDialog()
            .onYesButtonClick(() => this.deleteAutomatedTaskDialog(action));
    }

    editOneTimeTask(task: ITaskV2) {
        this.fiChecklist2ReportDialogsService
            .showDeviationOneTimeTaskDialog(
                this.data.answerDetailsReport.CheckpointId,
                this.data.accessRightFilterCollection,
                this.data.isTaskV2,
                task,
            )
            .onYesButtonClick((newTask: ITaskV2) => {
                // todo
                this.fiTaskSharedService.saveTask(newTask, this.data.isTaskV2).then((taskId: number) => {
                    newTask.Id = taskId;

                    this.updateOneTimeTasks((oneTimeTasks) => {
                        const taskIndex: number = oneTimeTasks.indexOf(task);
                        oneTimeTasks[taskIndex] = newTask;
                    });

                    this.fiChecklistHelperService.showActionAddedSnackbar(
                        newTask.Name,
                        this.data.answerDetailsReport.CheckpointName,
                    );
                    this.fiChecklist2ReportHelperService.triggerOneTimeTasksChanged(this.data.answerDetailsReport);
                    this.cdr.markForCheck();
                });
            })
            .afterClosed(() => (this.saveChangeWarningService.closeDialogOnTransition = false));
        this.saveChangeWarningService.closeDialogOnTransition = true;
    }

    openConfirmDeleteOneTimeTaskDialog(task: ITaskV2) {
        this.fiChecklist2ReportDialogsService
            .showConfirmDeleteOneTimeTask()
            .onYesButtonClick(() => this.deleteOneTimeTask(task));
    }

    openConfirmArchiveOneTimeTaskDialog(task: ITaskV2) {
        this.fiChecklist2ReportDialogsService
            .showConfirmArchiveOneTimeTask()
            .onYesButtonClick(() => this.archiveOneTimeTask(task));
    }

    private updateOneTimeTasks(updateFn: (tasks: ITaskV2[]) => void) {
        const oneTimeTasks = this.oneTimeTasks$.value;
        updateFn(oneTimeTasks);
        this.data.answerDetailsReport.OneTimeTaskIds = oneTimeTasks.map((task) => task.Id);
        this.oneTimeTasks$.next(oneTimeTasks);
    }

    private deleteOneTimeTask(task: ITaskV2) {
        this.fiTaskV2Service.deleteTasks([task.Id], this.data.isTaskV2).then(() => {
            this.removeOneTimeTaskFromArray(task);
        });
    }

    private archiveOneTimeTask(task: ITaskV2) {
        this.fiTaskV2Service.simpleArchiveTask([task.Id], true).then(() => {
            this.removeOneTimeTaskFromArray(task);
        });
    }

    private removeOneTimeTaskFromArray(task: ITaskV2) {
        this.updateOneTimeTasks((oneTimeTasks) => {
            const taskIndex: number = oneTimeTasks.indexOf(task);
            oneTimeTasks.splice(taskIndex, 1);
        });
        this.cdr.markForCheck();
        this.fiChecklist2ReportHelperService.triggerOneTimeTasksChanged(this.data.answerDetailsReport);
    }

    private deleteAutomatedTaskDialog(action: ICheckpointAction) {
        this.fiChecklist2Service.deleteCheckpointAction(action.Id).then(() => {
            const actionIndex: number = this.data.answerDetailsReport.CheckpointActions.indexOf(action);
            this.data.answerDetailsReport.CheckpointActions.splice(actionIndex, 1);
            this.fiSnackbarService.showSnackbar({
                textMessage: this.appTextService.get('checklist.checkpoint_action_deleted'),
            });
            this.fiChecklist2ReportHelperService.triggerCheckpointActionsChanged(this.data.answerDetailsReport);
            this.cdr.markForCheck();
        });
    }

    getSelectedAutomatedActionItemIds(alertRecipient: ICheckpointActionAlertRecipient): number[] {
        return this.fiCheckpoint2HelperService.getCheckpointTaskRecipientIds(alertRecipient);
    }

    getSelectedOneTimeTaskItemIds(task: ITask): number[] {
        return this.isTaskRecipientUsers(task)
            ? task.AccessRightFilterCollection.AccessRightUserFilter.UserIds
            : task.AccessRightFilterCollection.AccessRightCoreFilter.RoleIds;
    }

    getOneTimeTaskRecipientType(task: ITask): CheckpointActionRecipientType {
        return this.isTaskRecipientUsers(task)
            ? CheckpointActionRecipientType.Users
            : CheckpointActionRecipientType.Roles;
    }

    private isTaskRecipientUsers(task: ITask): boolean {
        return !!task.AccessRightFilterCollection.AccessRightUserFilter.UserIds?.length;
    }

    isPrivateTask(task: ITask): boolean {
        return this.fiTaskHelperService.isPrivate(task);
    }

    addCheckpointAction(checkpointActionType: CheckpointActionType) {
        const action: ICheckpointAction = this.fiCheckpoint2HelperService.getDefaultAction(checkpointActionType);

        this.getCheckpointActionEditorDialogByType(action)
            .onYesButtonClick((action: ICheckpointAction) => {
                const actionIndex: number = this.data.answerDetailsReport.CheckpointActions?.length | 0;
                this.saveSingleCheckpointAction(this.data.answerDetailsReport, action, actionIndex);
            })
            .afterClosed(() => (this.saveChangeWarningService.closeDialogOnTransition = false));
        this.saveChangeWarningService.closeDialogOnTransition = true;
    }

    private getCheckpointActionEditorDialogByType(
        checkpointAction: ICheckpointAction,
    ): IFiDialogResult<ICheckpointAction> {
        return this.fiCheckpoint2HelperService.getCheckpointActionEditorDialogByType(
            checkpointAction,
            this.data.accessRightFilterCollection,
            false,
            this.data.answerDetailsReport.CheckpointName,
            this.data.answerDetailsReport.ChecklistName,
        );
    }

    addCheckpointOneTimeTask() {
        this.fiChecklist2ReportDialogsService
            .showDeviationOneTimeTaskDialog(
                this.data.answerDetailsReport.AnswerId,
                this.data.accessRightFilterCollection,
                this.data.isTaskV2,
                undefined,
                this.data.answerDetailsReport.ChangedByProfile.Id,
            )
            .onYesButtonClick((oneTimeTask: ITaskV2) => {
                this.fiTaskSharedService.saveTask(oneTimeTask, this.data.isTaskV2).then((taskId) => {
                    oneTimeTask.Id = taskId;

                    this.updateOneTimeTasks((oneTimeTasks) => {
                        oneTimeTasks.push(oneTimeTask);
                    });

                    // now we should update OneTimeTasks in other components
                    this.fiChecklist2ReportHelperService.triggerOneTimeTasksChanged(this.data.answerDetailsReport);
                    this.cdr.markForCheck();
                });
            })
            .afterClosed(() => (this.saveChangeWarningService.closeDialogOnTransition = false));
        this.saveChangeWarningService.closeDialogOnTransition = true;
    }

    closeDialog() {
        this.dialogRef.close();
    }

    private fiChecklistHelperService: FiChecklistHelperService;
    private fiCheckpoint2HelperService: FiCheckpoint2HelperService;
    private fiChecklist2Service: FiChecklist2Service;
    private cdr: ChangeDetectorRef;
    private fiSnackbarService: FiSnackBarService;
    private appTextService: AppTextService;
    private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService;
    private fiTaskSharedService: FiTaskSharedService;
    private fiTaskHelperService: FiTaskHelperService;
    private fiTaskV2Service: FiTaskV2Service;
    private fiChecklist2ReportDialogsService: FiChecklist2ReportDialogsService;
    private saveChangeWarningService: SaveChangesWarningService;

    private setServices() {
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
        this.fiCheckpoint2HelperService = this.injector.get(FiCheckpoint2HelperService);
        this.fiChecklist2Service = this.injector.get(FiChecklist2Service);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiSnackbarService = this.injector.get(FiSnackBarService);
        this.appTextService = this.injector.get(AppTextService);
        this.fiChecklist2ReportHelperService = this.injector.get(FiChecklist2ReportHelperService);
        this.fiTaskSharedService = this.injector.get(FiTaskSharedService);
        this.fiTaskHelperService = this.injector.get(FiTaskHelperService);
        this.fiTaskV2Service = this.injector.get(FiTaskV2Service);
        this.fiChecklist2ReportDialogsService = this.injector.get(FiChecklist2ReportDialogsService);
        this.saveChangeWarningService = this.injector.get(SaveChangesWarningService);
    }

    protected readonly CheckpointActionType = CheckpointActionType;
    protected readonly ObjectTypes = ObjectTypes;
}
